import _ from 'lodash';
import { createAxios } from '../lib/axiosFactory';
import { Contacts as ContactConstants } from '../lib/constants';
import { dataCollectionParser } from '../lib/response/dataParser';
import { errorParser } from '../lib/response/errorParser';
import { VuexLocalStorage } from '../lib/vuexLocalStorage';
import {
  CommunicationPreference,
  CommunicationPreferenceUpdateOrigin
} from '../lib/enums/communicationPreference';
import { AxiosResponse } from 'axios';
import { Pages } from '../lib/enums/pages';

const storage = new VuexLocalStorage('_USER_');
const defaultPageSize = 25;

export const state = {
  contacts: [],
  columns: [],
  totalCount: null,
  totalSubscribedCount: null,
  totalUnsubscribedCount: null,
  totalPendingCount: null,
  selectedContacts: [],
  deselectedContacts: [],
  addContactDialog: {
    action: ContactConstants.Add,
    show: false,
    headingText: '',
    submitText: ''
  },
  prevPages: [],
  currentPage: {},
  nextPages: [],
  newlySubscribedContact: null,
  firstContact: storage.get('firstContact')
    ? JSON.parse(storage.get('firstContact') as any)
    : null,
  firstSubscribedContact: storage.get('firstSubscribedContact')
    ? JSON.parse(storage.get('firstSubscribedContact') as any)
    : null
};

export const mutations = {
  setContacts(state: any, contacts: any[]): void {
    state.contacts = contacts;
  },

  setFirstContact(state: any, firstContact: any): void {
    storage.set(state, { firstContact }, 'firstContact', true);
  },

  setFirstSubscribedContact(state: any, firstSubscribedContact: any): void {
    storage.set(
      state,
      { firstSubscribedContact },
      'firstSubscribedContact',
      true
    );
  },

  setColumns(state: any, columns: any[]): void {
    state.columns = columns;
  },

  setTotalSubscribedCount(state: any, count: number): void {
    state.totalSubscribedCount = count;
  },

  setTotalUnsubscribedCount(state: any, count: number): void {
    state.totalUnsubscribedCount = count;
  },

  setTotalPendingCount(state: any, count: number): void {
    state.totalPendingCount = count;
  },

  setTotalCount(state: any, count: number): void {
    state.totalCount = count;
  },

  setPages(state: any, pageLinks: any): void {
    state.prevPages = pageLinks.previousPages;
    state.currentPage = pageLinks.currentPage;
    state.nextPages = pageLinks.nextPages;
  },

  addContact(state: any, contact: any): void {
    state.contacts.push(contact);
  },

  setContact(state: any, contact: any): void {
    const contactIndex = state.contacts.findIndex(
      (x: any) => x.id === contact.id
    );
    if (contactIndex >= 0) {
      state.contacts.splice(contactIndex, 1, contact);
      state.selectedContacts.splice(0, 1, contact);
    }
  },

  removeContact(state: any, contact: any): void {
    const contactIndex = state.contacts.findIndex(
      (x: any) => x.id === contact.id
    );
    if (contactIndex >= 0) {
      state.contacts.splice(contactIndex, 1);
      state.selectedContacts.splice(0, 1);
    }
  },

  setSelectedContacts(state: any, selectedContacts: any[]): void {
    state.selectedContacts = selectedContacts;
  },

  setDeselectedContacts(state: any, deselectedContacts: any[]): void {
    state.deselectedContacts = deselectedContacts;
  },

  setAddContactDialog(state: any, addContactDialog: any): void {
    state.addContactDialog = addContactDialog;
  },

  setNewlySubscribedContact(state: any, newlySubscribedContact: any): void {
    state.newlySubscribedContact = newlySubscribedContact;

    if (state.firstContact?.id === newlySubscribedContact?.id) {
      const firstContact = newlySubscribedContact;
      storage.set(state, { firstContact }, 'firstContact', true);
    }
    if (state.firstSubscribedContact?.id === newlySubscribedContact?.id) {
      const firstSubscribedContact = newlySubscribedContact;
      storage.set(
        state,
        { firstSubscribedContact },
        'firstSubscribedContact',
        true
      );
    }
  }
};

export const getters = {
  // This returns the view objects (mapping select value to to text for display purposes)
  contactsView(state): any[] {
    return state.contacts.map(x => {
      const newContact = _.cloneDeep(x);
      Object.keys(newContact).forEach(key => {
        if (state.columns[key]?.dataType === 'select') {
          newContact[key] = state.columns[key].data.find(
            x => x.value === newContact[key]
          )?.text;
        }
        if (newContact.communication_preference_meta)
          newContact.unsubscribeReason = getUnsubscribeReason(
            newContact.communication_preference_meta
          );
      });
      return newContact;
    });
  }
};

export const actions = {
  async loadContacts(
    { commit, rootState, dispatch },
    {
      endpoint,
      listId,
      searchTerm,
      communicationPreference,
      sort
    }: {
      endpoint?: string;
      listId?: string;
      searchTerm?: string;
      communicationPreference?: string;
      sort?: string;
    } = {}
  ): Promise<void> {
    try {
      const response = await dispatch('getContacts', {
        endpoint,
        listId,
        searchTerm,
        communicationPreference,
        sort
      });

      const { links, meta } = response.data;
      commit('setContacts', dataCollectionParser(response));
      commit('setTotalCount', meta.totalRecords);
      commit('setTotalSubscribedCount', meta.totalSubscribedRecords);
      commit('setTotalUnsubscribedCount', meta.totalUnsubscribedRecords);
      commit('setTotalPendingCount', meta.totalPendingRecords);
      commit('setColumns', meta.columns);
      commit('setPages', links);
    } catch (error) {
      commit('setContacts', []);
      commit('setColumns', []);
    }
  },

  async getContacts(
    { commit, rootState },
    {
      endpoint,
      listId,
      searchTerm,
      communicationPreference,
      pageSize,
      sort
    }: {
      endpoint?: string;
      listId?: string;
      searchTerm?: string;
      communicationPreference?: string;
      pageSize?: number;
      sort?: string;
    } = {}
  ): Promise<AxiosResponse> {
    let response;
    if (endpoint) {
      response = await createAxios().get(endpoint);
    } else {
      const size: number =
        pageSize ?? rootState.user.pageSizes[Pages.Contacts] ?? defaultPageSize;

      // exclude dashes and brackets from the search string to search phones pasted with those characters
      // TODO: deal with this on the backend?
      const strippedSearch = searchTerm?.replace(/(-|\(|\))/g, '');

      response = await createAxios().get('/backend/contacts-index', {
        params: {
          'page[limit]': size,
          listId: listId,
          sort: sort ?? 'createdAt:desc',
          ...(searchTerm && {
            'filter[__search:first_name,last_name,phone][val]': strippedSearch,
            'filter[__search:first_name,last_name,phone][op]': 'search'
          }),
          ...(communicationPreference && {
            'filter[communication_preference][val]': communicationPreference,
            'filter[communication_preference][op]': 'eq'
          })
        }
      });
    }
    return response;
  },

  async loadTotalSubscribedCount({ commit }): Promise<void> {
    try {
      const response = await createAxios().get('/backend/contacts-count-show');
      commit(
        'setTotalSubscribedCount',
        response.data.data.attributes.totalRecords
      );
    } catch (error) {
      commit('setTotalSubscribedCount', null);
    }
  },

  async loadFirstContact({ commit }): Promise<void> {
    try {
      const response = await createAxios().get('/backend/contacts-index', {
        params: {
          'page[limit]': 1,
          sort: 'createdAt'
        }
      });

      const firstContact = dataCollectionParser(response).pop();

      commit('setFirstContact', firstContact);
    } catch (error) {
      commit('setFirstContact', null);
    }
  },

  async loadFirstSubscribedContact({ commit }): Promise<void> {
    try {
      const response = await createAxios().get('/backend/contacts-index', {
        params: {
          'page[limit]': 1,
          sort: 'createdAt',
          'filter[communication_preference][val]':
            CommunicationPreference.Subscribed,
          'filter[communication_preference][op]': 'eq'
        }
      });

      const firstSubscribedContact = dataCollectionParser(response).pop();

      commit('setFirstSubscribedContact', firstSubscribedContact);
    } catch (error) {
      commit('setFirstSubscribedContact', null);
    }
  },

  async loadContactsFromAudience(
    { commit, rootState },
    { campaignId }: { campaignId: string }
  ): Promise<void> {
    try {
      const response = await createAxios().get(
        '/backend/campaign-audience-contacts-index',
        {
          params: {
            campaignId: campaignId
          }
        }
      );

      commit('setContacts', response.data.data);
    } catch (error) {
      commit('setContacts', []);
    }
  },

  async createContact(context: any, payload: any) {
    const responseData = { success: false, message: '', errors: {} };
    try {
      await createAxios().post('/backend/contacts-create', {
        data: {
          type: 'contacts',
          attributes: payload
        }
      });

      return { ...responseData, success: true };
    } catch (error) {
      return {
        success: false,
        errors: errorParser(error)
      };
    }
  },

  updateSelectedContacts(context: any, payload: any) {
    context.commit('setSelectedContacts', payload);
  },

  updateDeselectedContacts(context: any, payload: any) {
    context.commit('setDeselectedContacts', payload);
  },

  updateAddContactDialog(context: any, payload: any) {
    context.commit('setAddContactDialog', payload);
  },

  async updateContact(context: any, payload: any) {
    const responseData = { success: false, message: '', errors: {} };
    try {
      const response: any = await createAxios().patch(
        '/backend/contacts-update',
        {
          data: {
            id: payload.id,
            type: 'contacts',
            attributes: payload
          }
        },
        {
          params: {
            id: payload.id,
            clientId: storage.get('clientId')
          }
        }
      );

      const responseContactData: any = response.data.data;
      context.commit('setContact', {
        ...{ id: responseContactData.id },
        ...responseContactData.attributes
      });
      responseData.success = true;

      return responseData;
    } catch (error) {
      return {
        success: false,
        errors: errorParser(error)
      };
    }
  },

  async deleteContact(context: any, payload: any) {
    let responseData = { success: false, message: '', errors: {} };
    try {
      const response: any = await createAxios().delete(
        '/backend/contacts-delete',
        {
          params: {
            id: payload.id
          }
        }
      );

      if (response && response.status === 204) {
        responseData.success = true;
      } else {
        responseData = {
          success: false,
          message: '',
          errors: { phone: 'Contact was not found' }
        };
      }

      return responseData;
    } catch (error) {
      return {
        success: false,
        errors: errorParser(error)
      };
    }
  },

  async updateContacts(context: any, payload: any) {
    try {
      const response = await createAxios().patch(
        '/backend/bulk-contacts-update',
        {
          data: payload
        },
        {
          params: {
            clientId: context.rootState.user.clientId
          }
        }
      );

      return { success: true, errors: {}, body: response.data };
    } catch (error) {
      return {
        success: false,
        errors: errorParser(error),
        body: error.response.data
      };
    }
  },

  async getContact(context: any, contactId) {
    try {
      const response = await createAxios().get('/backend/contacts-show', {
        params: {
          id: contactId
        }
      });

      return { success: true, errors: {}, body: response.data };
    } catch (error) {
      return {
        success: false,
        errors: errorParser(error),
        body: error.response.data
      };
    }
  },

  async deleteContacts(
    context: any,
    { payload, mode, searchTerm, communication_preference }
  ) {
    try {
      const response = await createAxios().patch(
        `/backend/bulk-contacts-delete`,
        {
          data: payload
        },
        {
          params: {
            mode: mode,
            ...(searchTerm && {
              'filter[__search:first_name,last_name,phone][val]': searchTerm,
              'filter[__search:first_name,last_name,phone][op]': 'search'
            }),
            'filter[communication_preference][val]': communication_preference,
            'filter[communication_preference][op]': 'eq'
          }
        }
      );

      return {
        success: true,
        errors: {},
        body: response.data,
        status: response.status
      };
    } catch (error) {
      return {
        success: false,
        errors: errorParser(error),
        body: error.response.data
      };
    }
  },

  async unsubscribeContact(context: any, payload: any) {
    try {
      const response = await createAxios().post(
        '/backend/contact-unsubscribes-create',
        {
          data: {
            type: 'contactUnsubscribes',
            attributes: {
              unsubscribeType: 'userViaSmart',
              contactId: payload.id
            }
          }
        }
      );

      return { success: true, errors: {}, body: response.data };
    } catch (error) {
      return {
        success: false,
        errors: errorParser(error),
        body: error.response.data
      };
    }
  },

  async bulkUnsubscribeContacts(context: any, payload: any) {
    try {
      const response = await createAxios().post(
        '/backend/campaign-audience-unsubscribe-create',
        {
          data: payload
        }
      );

      return { success: true, errors: {}, body: response.data };
    } catch (error) {
      return {
        success: false,
        errors: errorParser(error),
        body: error.response.data
      };
    }
  },

  async requestDownload(
    context: any,
    { communicationPreference, searchTerm = '', listId = undefined }
  ) {
    try {
      const response = await createAxios().post(
        '/backend/contact-downloads-create',
        {},
        {
          params: {
            ...(communicationPreference && {
              'filter[communication_preference][val]': communicationPreference,
              'filter[communication_preference][op]': 'eq'
            }),
            ...(searchTerm && {
              'filter[__search:first_name,last_name,phone][val]': searchTerm,
              'filter[__search:first_name,last_name,phone][op]': 'search'
            }),
            listId
          }
        }
      );

      return { success: true, errors: {}, body: response.data };
    } catch (error) {
      return {
        success: false,
        errors: errorParser(error),
        body: error.response.data
      };
    }
  }
};

function getUnsubscribeReason(
  reason: CommunicationPreferenceUpdateOrigin
): string {
  switch (reason) {
    case CommunicationPreferenceUpdateOrigin.ClientViaExternalApi:
      return 'API';
    case CommunicationPreferenceUpdateOrigin.ClientViaShopify:
      return 'Shopify';
    case CommunicationPreferenceUpdateOrigin.UserViaSmart:
    case CommunicationPreferenceUpdateOrigin.ClientViaInbox:
      return 'Manual';
    case CommunicationPreferenceUpdateOrigin.UserViaZapier:
      return 'Zapier';
    case CommunicationPreferenceUpdateOrigin.ContactViaSender:
    case CommunicationPreferenceUpdateOrigin.ContactViaLink:
      return 'Contact';
    case CommunicationPreferenceUpdateOrigin.SmartByNumberLookup:
    case CommunicationPreferenceUpdateOrigin.ClientByNumberLookup:
      return 'Number lookup';
    case CommunicationPreferenceUpdateOrigin.ClientViaOptOutImport:
      return 'Opt-out list';
    case CommunicationPreferenceUpdateOrigin.SmartDueToUnreachableNumber:
      return 'Unreachable';
    case CommunicationPreferenceUpdateOrigin.SmartViaAdminApi:
      return 'Admin';
    default:
      return 'System';
  }
}
